import React from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

/**
 * 动态主题 Provider：监听 data-app-theme 属性变化，切换 Ant Design 亮/暗算法。
 * 通过 MutationObserver 观察 html 元素上的属性，避免在 rootContainer 层使用 useModel。
 */
const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = React.useState(
    () => localStorage.getItem('navTheme') === 'dark',
  );

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-app-theme') === 'dark');
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-app-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677FF',
          colorSuccess: '#22c55e',
          colorWarning: '#f97316',
          colorError: '#ef4444',
          borderRadius: 6,
          borderRadiusXS: 2,
          borderRadiusSM: 6,
          borderRadiusLG: 14,
          padding: 16,
          paddingSM: 12,
          paddingLG: 24,
          margin: 16,
          marginSM: 12,
          marginLG: 24,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
          boxShadowSecondary: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        },
        components: {
          Button: {
            primaryShadow: 'none',
            defaultShadow: 'none',
            dangerShadow: 'none',
            borderRadius: 6,
          },
          Input: {
            activeShadow: 'none',
            borderRadius: 6,
          },
          Select: {
            optionSelectedFontWeight: 500,
            borderRadius: 6,
          },
          Alert: { borderRadiusLG: 8 },
          Modal: { borderRadiusLG: 12 },
          Steps: { iconSize: 32 },
          Switch: {
            trackHeight: 24,
            trackMinWidth: 44,
            innerMinMargin: 4,
            innerMaxMargin: 24,
          },
          Checkbox: { borderRadiusSM: 4 },
          Slider: {
            handleSize: 18,
            handleSizeHover: 20,
            railSize: 6,
          },
          ColorPicker: { borderRadius: 6 },
          Menu:{
            itemHeight: 48
          },
          "Tree": {
            "titleHeight": 32,
            "indentSize":16
          }
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

export default ThemeProvider;
